/**
 * Accès données maisons Bocuse. Source = backend public Alterrego, groupe "bocuse"
 * forcé sur ?source=ciss-api (mapping Ciss 230). Identique à l'appel V0, éprouvé
 * en prod. Pas d'endpoint détail dédié → la fiche réutilise l'item de la liste.
 */
import { useEffect, useState } from 'react';
import type { Merchant } from '@alterrego/module-merchants-directory';

const BACKEND_URL = 'https://api.apps.alterrego.com';
const GROUP_ID =
  (import.meta.env.VITE_MAISONS_BOCUSE_GROUP_ID as string | undefined) || 'bocuse';

// Entrées administratives masquées du portail public (siège, direction…).
const ADMIN_NAME_RE = /si[èe]ge|direction/i;

export interface MaisonsState {
  maisons: Merchant[];
  loading: boolean;
  error: boolean;
}

let cache: Merchant[] | null = null;

/** Charge (et met en cache mémoire) la liste filtrée + triée des maisons. */
export function useMaisons(): MaisonsState {
  const [maisons, setMaisons] = useState<Merchant[]>(cache ?? []);
  const [loading, setLoading] = useState(cache === null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (cache !== null) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/merchants-directory/groups/${encodeURIComponent(GROUP_ID)}?source=ciss-api`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items: Merchant[] = Array.isArray(data.items) ? data.items : [];
        const visible = items
          .filter((m) => m.status !== 'inactive' && !ADMIN_NAME_RE.test(m.name))
          .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
        cache = visible;
        if (alive) {
          setMaisons(visible);
          setLoading(false);
        }
      } catch {
        if (alive) {
          setError(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return { maisons, loading, error };
}

/** Sélectionne une maison par id dans la liste chargée. */
export function useMaison(id: string | null): { maison: Merchant | null } & MaisonsState {
  const state = useMaisons();
  const maison = id ? state.maisons.find((m) => m.id === id) ?? null : null;
  return { ...state, maison };
}
